import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ContactType} from "../comment/comment.model";
import {ConfirmationService, MessageService} from "primeng/api";
import {SocialSupportService} from "./social-support.service";
import {CommentService} from "../comment/comment.service";
import {FileUpload} from "primeng/fileupload";
import {Attachment} from "../attachments/attachment.model";
import {AuthService} from "../../../login/auth.service";
import {RoleEnum} from "../../../login/role.enum";

@Component({
    selector: 'app-social-support',
    templateUrl: './social-support.component.html',
    styleUrls: ['./social-support.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class SocialSupportComponent implements OnInit {
  socialSupportService = inject(SocialSupportService);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  commentService = inject(CommentService);

  readonly acceptedFileTypes = 'application/pdf,.pdf';

  noteForm = new FormGroup({
    contact_type: new FormControl<ContactType | null>(null),
    text: new FormControl('')
  });
  @Input() customerId!: number;
  loading = true;

  selectedFile?: File;
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  attachmentForm = new FormGroup({
    name: new FormControl('', Validators.required)
  })

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.socialSupportService.fetchSocialSupportNotes(this.customerId),
      this.fetchAttachments()
    ]);
    this.loading = false;
  }

  async saveNote(): Promise<void> {
    const {contact_type, text} = this.noteForm.value;
    if (contact_type && text) {
      try {
        await this.socialSupportService.createSocialSupportNote(contact_type, text, this.customerId);
        this.messageService.add({severity: 'success', summary: 'Bemerkung gespeichert'});
        this.noteForm.reset();
        this.loading = true;
        await this.socialSupportService.fetchSocialSupportNotes(this.customerId);
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.messageService.add({severity: 'error', summary: 'Fehler beim Speichern der Bemerkung'});
      }
    }
  }

  async deleteNote(id: string, event: Event): Promise<void> {
    this.confirmationService.confirm({
      target: event.target ? event.target : undefined,
      message: 'Möchtest du diese Bemerkung wirklich löschen?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "JA",
      rejectLabel: "NEIN",
      accept: async () => {
        try {
          await this.socialSupportService.deleteSocialSupportNote(id);
          this.messageService.add({severity: 'success', summary: 'Bemerkung gelöscht'});
          this.loading = true;
          await this.socialSupportService.fetchSocialSupportNotes(this.customerId);
          this.loading = false;
        } catch (e) {
          console.error(e);
          this.messageService.add({severity: 'error', summary: 'Fehler beim Löschen der Bemerkung'});
        }
      }
    });
  }

  async deleteAttachment(id: number, event: Event) {
    if (!this.canDeleteFiles) {
      return;
    }

    this.confirmationService.confirm({
      target: event.target ? event.target : undefined,
      message: 'Bist du sicher, dass du diese Datei löschen möchtest?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'JA',
      rejectLabel: 'NEIN',
      accept: async () => {
        try {
          await this.socialSupportService.deleteAttachment(id);
          await this.fetchAttachments();
          this.messageService.add({severity: 'success', summary: 'Erfolg', detail: 'Datei wurde gelöscht.'});
        } catch (e) {
          console.error(e);
          this.messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Datei konnte nicht gelöscht werden.'
          });
        }
      },
    });
  }

  async download(attachment: Attachment) {
    try {
      await this.socialSupportService.downloadAttachment(attachment);
      this.messageService.add({severity: 'success', summary: 'Erfolg', detail: 'Datei wurde heruntergeladen.'});
    } catch (e) {
      console.error(e);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Datei konnte nicht heruntergeladen werden.'
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFileSelect(event: any) {
    const file = event.files[0];

    if (!this.isPdfFile(file)) {
      this.selectedFile = undefined;
      this.fileUpload.clear();
      this.messageService.add({severity: 'error', summary: 'Fehler', detail: 'Es können nur PDF-Dateien hochgeladen werden.'});
      return;
    }

    this.selectedFile = file;
  }

  async saveFile() {
    const {name} = this.attachmentForm.value;

    if (!name || !this.selectedFile) {
      this.attachmentForm.get('name')?.markAsDirty();
      return;
    }

    if (!this.isPdfFile(this.selectedFile)) {
      this.messageService.add({severity: 'error', summary: 'Fehler', detail: 'Es können nur PDF-Dateien hochgeladen werden.'});
      return;
    }

    try {
      await this.socialSupportService.createAttachment(this.customerId, name, this.selectedFile);
      await this.fetchAttachments();
      this.messageService.add({severity: 'success', summary: 'Erfolg', detail: 'Datei wurde hochgeladen.'});

      this.attachmentForm.reset();
      this.selectedFile = undefined;
      this.fileUpload.clear();
    } catch (e) {
      console.error(e);
      this.messageService.add({severity: 'error', summary: 'Fehler', detail: 'Datei konnte nicht hochgeladen werden.'});
    }
  }

  async fetchAttachments() {
    this.loading = true;
    await this.socialSupportService.fetchAttachments(this.customerId);
    this.loading = false;
  }

  get canDeleteFiles(): boolean {
    const role = this.authService.user()?.role;
    return role?.id === RoleEnum.Admin || role?.type === 'admin';
  }

  private isPdfFile(file?: File): boolean {
    return !!file && file.type === 'application/pdf' && file.name.toLowerCase().endsWith('.pdf');
  }
}
